import React, { useEffect, useState } from "react";
import {
  conflictRules,
  createPositionCodeAdmin,
  deletePositionCode,
  getPositionCodes,
  getServiceIDsForPositionCode,
  positionCodeEquals,
  setsEqual,
  updatePositionCodeAdmin,
  updateServicesForPositionCodeAdmin,
} from "../../services/positionCodeService";
import { getClient } from "../../services/clientService";
import { PositionCode } from "../../interfaces/positionCode";
import { Service } from "../../interfaces/service";
import { getAllServices } from "../../services/serviceService";
import { Client } from "../../interfaces/client";
import { useParams } from "react-router-dom";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import { sortServices } from "../../services/helpers";

const AdminPositionCodes = () => {
  const { clientID } = useParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [OGpositionCodes, setOGPositionCodes] = useState<PositionCode[] | null>(
    null,
  );
  const [positionCodes, setPositionCodes] = useState<PositionCode[] | null>(
    null,
  );
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [OGselectedServices, setOGSelectedServices] = useState<
    Map<number, Set<number>>
  >(new Map());
  const [selectedServices, setSelectedServices] = useState<
    Map<number, Set<number>>
  >(new Map());
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [localPositionCodeIDs, setLocalPositionCodeIDs] = useState<Set<number>>(
    new Set(),
  );
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const loadPositionCodes = async () => {
      try {
        if (clientID) {
          const numericClientID = parseInt(clientID, 10);
          const client = await getClient(numericClientID);
          setClient(client);

          const positionCodes = await getPositionCodes(numericClientID);
          setPositionCodes(positionCodes);
          setOGPositionCodes(positionCodes);
          const allServices = await getAllServices();
          const sortedServices = await sortServices(allServices);
          setAllServices(sortedServices);

          const serviceMap = new Map();
          for (const positionCode of positionCodes) {
            const serviceIDs = await getServiceIDsForPositionCode(
              positionCode.positionCodeID,
            );
            serviceMap.set(positionCode.positionCodeID, new Set(serviceIDs));
          }
          setSelectedServices(serviceMap);
          setOGSelectedServices(serviceMap);
        }
      } catch (error) {
        console.error("Error fetching position codes or services:", error);
        setErrorMessage("Error fetching data.");
      } finally {
        setLoading(false);
      }
    };

    loadPositionCodes();
  }, [clientID]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  const handleCheckboxChange = (positionCodeID: number, serviceID: number) => {
    setEditing(true);
    setSelectedServices((prevSelected) => {
      const newSelected = new Map(prevSelected);
      const selectedServiceSet = new Set(newSelected.get(positionCodeID) || []);

      const isSelected = selectedServiceSet.has(serviceID);
      const conflictingServices = conflictRules[serviceID] || [];

      if (!isSelected) {
        const foundConflict = conflictingServices.find((conflictID) =>
          selectedServiceSet.has(conflictID),
        );

        if (foundConflict) {
          setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [positionCodeID]: `You have a conflicting selected service.`,
          }));
          return prevSelected;
        }
      }

      if (isSelected) {
        selectedServiceSet.delete(serviceID);
      } else {
        selectedServiceSet.add(serviceID);
      }

      newSelected.set(positionCodeID, selectedServiceSet);

      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        [positionCodeID]: "",
      }));

      return newSelected;
    });
  };

  const handleSave = async () => {
    try {
      const errors: Record<string, string> = {};

      if (!positionCodes || positionCodes.length === 0) return;

      for (let i = 0; i < positionCodes.length; i++) {
        const positionCode = positionCodes[i];

        if (!positionCode) continue;

        // Validate fields for each position code
        if (!positionCode.positionCodeTitle) {
          errors[`positionCode${i}_positionCodeTitle`] = "Title is required.";
        }
        if (!positionCode.referenceLookbackYears) {
          errors[`positionCode${i}_referenceLookbackYears`] =
            "Years are required.";
        }

        const selectedServiceIDs = Array.from(
          selectedServices.get(positionCode.positionCodeID) || [],
        );
        if (selectedServiceIDs.length === 0) {
          errors[`positionCode${i}_services`] =
            "At least one service must be selected.";
        }
      }

      setValidationErrors((prevErrors) => ({ ...prevErrors, ...errors }));

      if (Object.keys(errors).length > 0) return;

      // Save all position codes (only the ones with changes)
      for (const positionCode of positionCodes) {
        const ogPC = OGpositionCodes?.find(
          (pc) => pc.positionCodeID === positionCode.positionCodeID,
        );
        const currentPCS =
          selectedServices.get(positionCode.positionCodeID) ??
          new Set<number>();
        const ogPCS =
          OGselectedServices.get(positionCode.positionCodeID) ??
          new Set<number>();

        const isNew = localPositionCodeIDs.has(positionCode.positionCodeID);

        if (isNew) {
          const created = await createPositionCodeAdmin(positionCode);
          await updateServicesForPositionCodeAdmin(
            created.positionCodeID,
            Array.from(currentPCS),
          );
          continue;
        }

        const pcChanged = !positionCodeEquals(positionCode, ogPC);
        const pcsChanged = !setsEqual(currentPCS, ogPCS);

        if (!pcChanged && !pcsChanged) {
          continue;
        }

        if (pcChanged) {
          await updatePositionCodeAdmin(positionCode);
        }

        if (pcsChanged) {
          await updateServicesForPositionCodeAdmin(
            positionCode.positionCodeID,
            Array.from(currentPCS),
          );
        }
      }
      setEditing(false);
      setSuccessMessage(`All position codes saved successfully.`);
      window.location.reload();
    } catch (error) {
      console.error("Error saving position codes:", error);
      setSuccessMessage("");
    }
  };

  const handleDelete = async (positionCodeID: number) => {
    console.log(positionCodeID);
    try {
      if (localPositionCodeIDs.has(positionCodeID)) {
        setPositionCodes(
          (prevCodes) =>
            prevCodes?.filter(
              (code) => code.positionCodeID !== positionCodeID,
            ) || [],
        );
        setLocalPositionCodeIDs((prevIDs) => {
          const updatedIDs = new Set(prevIDs);
          updatedIDs.delete(positionCodeID);
          return updatedIDs;
        });
        setSuccessMessage("Position Code deleted successfully.");
      } else {
        await deletePositionCode(positionCodeID);
        setPositionCodes(
          (prevCodes) =>
            prevCodes?.filter(
              (code) => code.positionCodeID !== positionCodeID,
            ) || [],
        );
        setSuccessMessage("Position Code deleted successfully.");
      }
    } catch (error) {
      console.error("Error deleting position code:", error);
      setSuccessMessage("");
    }
  };

  const handleCreatePositionCode = () => {
    setEditing(true);
    const newPositionCode: PositionCode = {
      positionCodeID: Date.now() % 2147483647,
      clientID: client?.clientID || 0,
      positionCodeTitle: "",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    };

    setPositionCodes((prevCodes) => [...(prevCodes || []), newPositionCode]);
    setLocalPositionCodeIDs((prevIDs) =>
      new Set(prevIDs).add(newPositionCode.positionCodeID),
    );

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`positionCode${positionCodes?.length}_positionCodeTitle`]:
        "Title is required.",
    }));
  };

  const renderServices = (
    positionCodeID: number,
    requiresCandidatePayment: boolean,
  ) => (
    <>
      <div className="services-list">
        {allServices.map((service) => (
          <div key={service.serviceID} className="service-item">
            <label>
              <input
                style={{ marginRight: "10px" }}
                className={`form-check-input ${
                  validationErrors[`positionCode${positionCodeID}_services`]
                    ? "is-invalid"
                    : ""
                }`}
                type="checkbox"
                checked={
                  selectedServices
                    .get(positionCodeID)
                    ?.has(service.serviceID) || false
                }
                onChange={() =>
                  handleCheckboxChange(positionCodeID, service.serviceID)
                }
                disabled={requiresCandidatePayment}
              />
              {service.serviceName}
            </label>
          </div>
        ))}
      </div>

      {validationErrors[`positionCode${positionCodeID}_services`] && (
        <div className="invalid-feedback">
          {validationErrors[`positionCode${positionCodeID}_services`]}
        </div>
      )}
    </>
  );

  const handlePositionCodeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
    index: number,
  ) => {
    setEditing(true);
    const value =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setPositionCodes(
      (prevPositionCodes) =>
        prevPositionCodes?.map((positionCode, i) =>
          i === index ? { ...positionCode, [field]: value } : positionCode,
        ) || [],
    );

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`positionCode${index}_${field}`]: "",
    }));
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Position Codes</h1>
        <div className="page-items-container">
          <div className="field-long">
            <label className="field-label">Client Name:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={client?.clientName || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="field-long">
            <label className="field-label">Abbreviation:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={client?.abbreviation}
                disabled
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="data-container">
            {errorMessage && (
              <div className="error-message">{errorMessage}</div>
            )}
            {positionCodes && positionCodes.length > 0 ? (
              positionCodes.map((positionCode, index) => (
                <div
                  key={positionCode.positionCodeID}
                  className="position-codes-group"
                >
                  <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
                    Position Code {index + 1}
                  </div>
                  {positionCode.requiresCandidatePayment && (
                    <div className="text-container error">
                      Should not be modified unless organization comes to new
                      agreement. Contact Solutia/Kaia to modify.
                    </div>
                  )}
                  <div className="field-long" style={{ flex: "0 0 60%" }}>
                    <label className="field-label">Title:</label>
                    <input
                      className={`form-control ${
                        validationErrors[
                          `positionCode${index}_positionCodeTitle`
                        ]
                          ? "is-invalid"
                          : ""
                      }`}
                      type="text"
                      value={positionCode.positionCodeTitle || ""}
                      onChange={(e) =>
                        handlePositionCodeChange(e, "positionCodeTitle", index)
                      }
                      disabled={positionCode.requiresCandidatePayment}
                    />
                    {validationErrors[
                      `positionCode${index}_positionCodeTitle`
                    ] && (
                      <div className="invalid-feedback">
                        {
                          validationErrors[
                            `positionCode${index}_positionCodeTitle`
                          ]
                        }
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      flex: "0 0 30%",
                      alignSelf: "end",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-around",
                    }}
                  >
                    <button
                      className={`oxbtn btn-red ${
                        positionCode.requiresCandidatePayment ? "disabled" : ""
                      }`}
                      onClick={() =>
                        setDeleteTargetId(positionCode.positionCodeID)
                      }
                      disabled={positionCode.requiresCandidatePayment}
                    >
                      Delete
                    </button>
                  </div>
                  <div className="field-long" style={{ flex: "0 0 20%" }}>
                    <label className="field-label">FCRA Compliance:</label>
                    <div>
                      <input
                        className={`form-check-input`}
                        type="checkbox"
                        checked={positionCode?.fcraCompliance || false}
                        onChange={(e) =>
                          handlePositionCodeChange(e, "fcraCompliance", index)
                        }
                        disabled={positionCode.requiresCandidatePayment}
                      />
                    </div>
                  </div>
                  <div className="field-long" style={{ flex: "0 0 20%" }}>
                    <label className="field-label">Crim Lookback Years:</label>
                    <div>
                      <input
                        className={`form-control ${
                          validationErrors[
                            `positionCode${index}_criminalLookbackYears`
                          ]
                            ? "is-invalid"
                            : ""
                        }`}
                        type="text"
                        value={positionCode.criminalLookbackYears || ""}
                        onChange={(e) =>
                          handlePositionCodeChange(
                            e,
                            "criminalLookbackYears",
                            index,
                          )
                        }
                        disabled={positionCode.requiresCandidatePayment}
                      />
                      {validationErrors[
                        `positionCode${index}_criminalLookbackYears`
                      ] && (
                        <div className="invalid-feedback">
                          {
                            validationErrors[
                              `positionCode${index}_criminalLookbackYears`
                            ]
                          }
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="services-container">
                    <div className="text-sub-sub-header">
                      Services to be Performed:
                    </div>
                    {renderServices(
                      positionCode.positionCodeID,
                      positionCode.requiresCandidatePayment,
                    )}
                    {validationErrors[`${positionCode.positionCodeID}`] && (
                      <div
                        className="invalid-feedback"
                        style={{ display: "block" }}
                      >
                        {validationErrors[`${positionCode.positionCodeID}`]}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p>No position codes available for this client.</p>
            )}
            {deleteTargetId !== null && (
              <ConfirmDelete
                message="Are you sure you want to delete this position code?"
                onConfirm={async () => {
                  await handleDelete(deleteTargetId);
                  setDeleteTargetId(null);
                }}
                onCancel={() => setDeleteTargetId(null)}
              />
            )}

            <div className="action-buttons" style={{ flex: "0 0 100%" }}>
              <button
                className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
                onClick={() => handleSave()}
                disabled={!editing}
              >
                Save
              </button>
              {positionCodes && positionCodes.length < 10 && (
                <button
                  className="oxbtn btn-purple"
                  onClick={() => handleCreatePositionCode()}
                >
                  Add Additional Position Code{" "}
                  <i className="bi bi-plus-lg btn-icon"></i>
                </button>
              )}
            </div>
          </div>

          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPositionCodes;
